import { useEffect, useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/probroker-logo.png";
import {
  LayoutDashboard, Home, Briefcase, Building2, Building,
  GitCompare, Users, CreditCard, LogOut, X, Settings, Bookmark, Crown, ChevronRight, Video, Search,MapPin
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useUserProfile } from "../contexts/UserProfileContext";
import { FEATURES } from "../constants/feature.constants";
import Cookies from "js-cookie";
import apiClient from "../common/apiClient";

const mainItems = [
  { en: "Dashboard", gu: "ડેશબોર્ડ", path: "/dashboard", icon: LayoutDashboard },
  { en: "Owner Properties", gu: "માલિક પ્રોપર્ટી", path: "/owner-properties", icon: Home },
  { en: "Broker Properties", gu: "બ્રોકર પ્રોપર્ટી", path: "/broker-properties", icon: Briefcase },
  // { en: "Builder Projects", gu: "બિલ્ડર પ્રોજેક્ટ્સ", path: "/residential-rental/builder", icon: Building2 },
  { en: "Match Requirement", gu: "જરૂરિયાત મેચ", path: "/match-property", icon: GitCompare},
  { en: "Saved Searches", gu: "સેવ્ડ સર્ચ", path: "/saved-searches", icon: Search},

   { en: "Verify Listing", gu: "Verify Listing", path: "/verify-listing", icon: MapPin, isNew: true },
  { en: "My Properties", gu: "મારી પ્રોપર્ટી", path: "/myproperty", icon: Building },
  { en: "Saved Properties", gu: "સેવ્ડ પ્રોપર્ટી", path: "/saved-properties", icon: Bookmark },
  { en: "Tutorials", gu: "ટ્યુટોરિયલ્સ", path: "https://www.youtube.com/watch?v=Tp8yKAQWt0M", icon: Video, isExternal: true },
];

const managementItems = [
  { en: "Staff Management", gu: "સ્ટાફ મેનેજમેન્ટ", path: "/staff", icon: Users },
  { en: "Subscription", gu: "સબ્સ્ક્રિપ્શન", path: "/subscription", icon: CreditCard},
];

// toolOnly mirrors AppSidebar: Verify Listing, name, Logout, nothing else.
export function MobileSidebar({ open, onClose, toolOnly = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { userProfile, hasFeature } = useUserProfile();

  // Lock body scroll when sidebar is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

  const normalizeText = (value) => String(value || "").trim().toLowerCase();

  const hasActiveFeatureAccess = Array.isArray(userProfile?.FeaturesAccessData)
    ? userProfile.FeaturesAccessData.some((item) => normalizeText(item?.status) === "active")
    : false;

  const profilePlanName =
    userProfile?.DemoAccount?.[0]?.PlanName ||
    userProfile?.DemoAccount?.[0]?.planName ||
    userProfile?.PlanName ||
    userProfile?.user?.PlanName ||
    userProfile?.plan ||
    userProfile?.user?.plan ||
    "";
  const isDemoAccount = useMemo(() => {
    // If user has payment history, they are NOT a demo account
    if (Array.isArray(userProfile?.paymentHistory) && userProfile.paymentHistory.length > 0) {
      return false;
    }

    const name = normalizeText(profilePlanName);
    return name === "demo account" || name === "demo_account";
  }, [profilePlanName, userProfile]);

  const currentPlanExpiry = useMemo(() => {
    if (isDemoAccount) {
      return (
        userProfile?.DemoPlanExpriDate ||
        userProfile?.DemoPlanExpriedDate ||
        userProfile?.user?.DemoPlanExpriDate ||
        userProfile?.user?.DemoPlanExpriedDate
      );
    }

    // 1. Check top-level expiry dates
    const topLevelExpiry =
      userProfile?.PlanExpriDate ||
      userProfile?.PlanExpriedDate ||
      userProfile?.user?.PlanExpriDate ||
      userProfile?.user?.PlanExpriedDate;

    if (topLevelExpiry) return topLevelExpiry;

    // 2. Check paidAddonsData for an active addon's EndDate
    if (Array.isArray(userProfile?.paidAddonsData) && userProfile.paidAddonsData.length > 0) {
      const activeAddon = userProfile.paidAddonsData.find(
        (a) => normalizeText(a.AddonStatus) === "active" || normalizeText(a.status) === "active"
      );
      const target = activeAddon || userProfile.paidAddonsData[0];
      return target?.EndDate || target?.expiredDate;
    }

    return null;
  }, [isDemoAccount, userProfile]);

  const calculateDaysLeft = () => {
    if (!currentPlanExpiry) return 0;
    const now = new Date();
    const endDate = new Date(currentPlanExpiry);
    const diffTime = endDate.getTime() - now.getTime();
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const daysLeft = calculateDaysLeft();
  const isExpired = hasActiveFeatureAccess && daysLeft <= 0;
  const planTag = isDemoAccount
    ? t("Demo", "ડેમો")
    : hasActiveFeatureAccess
      ? (isExpired ? t("Expired", "સમાપ્ત") : t("Premium", "પ્રીમિયમ"))
      : t("Free", "ફ્રી");
  const totalDays = 30; // Default baseline for progress bar
  const daysPercent = Math.min(100, Math.max(0, (daysLeft / totalDays) * 100));

  const userName = Cookies.get("name") || "User";
  const userNumber = Cookies.get("number") || "";

  const handleLogout = async () => {
    const token = Cookies.get("token");

    // Clear local state immediately for better UX
    const cookiesToClear = [
      "token", "userId", "bid", "isPremium", "name",
      "number", "companyName", "planExpiry", "permission", "deviceId",
      "propertyAccessCity"
    ];

    // Attempt API logout if token exists
    if (token) {
      try {
        await apiClient(
          "get",
          "/cjidnvij/ceksfbuebijn/user/v2/logout/dljcnji/cekbjid",
          null,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      } catch (error) {
        console.error("Logout API failed:", error);
      }
    }

    // Clear cookies and storage
    cookiesToClear.forEach(cookie => Cookies.remove(cookie));
    localStorage.clear();

    // Redirect to landing page or login
    window.location.href = "/";
  };

  const renderNavItem = (item) => {
    const isActive = location.pathname === item.path;

    // Feature-based disabling
    let isFeatureDisabled = false;
    if (item.path === "/owner-properties") isFeatureDisabled = !hasFeature(FEATURES.OWNER_PROPERTY);
    if (item.path === "/broker-properties") isFeatureDisabled = !hasFeature(FEATURES.BROKER_PROPERTY);
    if (item.path === "/residential-rental/builder") isFeatureDisabled = !hasFeature(FEATURES.BUILDER_PROPERTY);

    const isDisabled = item.disabled || isFeatureDisabled;

    if (isDisabled) {
      return (
        <div
          key={item.path}
          className="flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg text-sm font-medium text-sidebar-foreground/40 cursor-not-allowed"
        >
          <item.icon size={20} className="flex-shrink-0" />
          <div className="flex items-center justify-between flex-1">
            <span>{t(item.en, item.gu)}</span>
            {(item.comingSoon || isFeatureDisabled) && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-bold uppercase tracking-wider">
                {item.comingSoon ? t("Soon", "ટૂંક સમયમાં") : t("Locked", "લોક")}
              </span>
            )}
          </div>
        </div>
      );
    }

    if (item.isExternal) {
      return (
        <a
          key={item.path}
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
          className="flex items-center gap-3 px-6 py-3 mx-0 rounded-none text-sm font-medium transition-all duration-200 text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground"
        >
          <item.icon size={20} className="flex-shrink-0" />
          <span>{t(item.en, item.gu)}</span>
        </a>
      );
    }

    return (
      <NavLink
        key={item.path}
        to={item.path}
        onClick={onClose}
        className={`relative overflow-hidden flex items-center gap-3 px-6 py-3 mx-0 rounded-none text-sm font-medium transition-all duration-200 ${isActive
          ? "bg-[hsl(262,83%,95%)] text-primary shadow-sm"
          : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground"
          }`}
      >
        <item.icon size={20} className="flex-shrink-0" />
        <span>{t(item.en, item.gu)}</span>
        {item.isNew && (
          <span className="chip-mirror-shine ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
            {t("New", "નવું")}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <div className={`fixed inset-0 z-[9999] md:hidden transition-all duration-300 ${open ? "visible" : "invisible"}`}>
      <div className={`absolute inset-0 bg-foreground/40 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`} onClick={onClose} />
      <aside className={`absolute right-0 top-0 bottom-0 w-[280px] bg-sidebar text-sidebar-foreground flex flex-col shadow-2xl transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}>
        <div className="h-16 flex items-center justify-end px-4 border-b border-sidebar-border">
          <button onClick={onClose} className="p-1 rounded-md hover:bg-sidebar-accent transition-colors">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 py-3 space-y-0.5 overflow-y-auto">
          {(toolOnly ? mainItems.filter((item) => item.path === "/verify-listing") : mainItems).map(renderNavItem)}

          {!toolOnly && (
          <div className="pt-4 pb-1">
            {Cookies.get("userHierarchy") === "main" && (
              <div className="px-4 mb-2 flex items-center gap-2">
                <Settings size={12} className="text-sidebar-foreground/50" />
                <span className="text-[10px] uppercase tracking-widest text-sidebar-foreground/50 font-semibold">
                  {t("Management", "મેનેજમેન્ટ")}
                </span>
              </div>
            )}
            {managementItems
              .filter(item => {
                // Hide Staff Management and Subscription for subusers
                const restrictedPaths = ["/staff", "/subscription"];
                if (Cookies.get("userHierarchy") === "subuser" && restrictedPaths.includes(item.path)) {
                  return false;
                }
                return true;
              })
              .map(renderNavItem)}
          </div>
          )}
        </nav>

        <div className="border-t border-sidebar-border p-3 space-y-2">
          {/* Profile */}
          <div className="flex items-center justify-between gap-3 px-2">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-sidebar-accent flex items-center justify-center text-sidebar-foreground font-bold text-sm flex-shrink-0">
                {userName.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{userName}</p>
              </div>
            </div>
            {!toolOnly && (
            <button
              onClick={() => {
                navigate("/my-profile");
                onClose();
              }}
              className="p-1 rounded-md hover:bg-sidebar-accent text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors"
            >
              <ChevronRight size={16} />
            </button>
            )}
          </div>

          {/* Plan Badge + Progress */}
          {!toolOnly && (
          <div className="px-2 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full gradient-primary text-primary-foreground">
                <Crown size={10} />
                {planTag}
              </span>
              <span className="text-[10px] text-sidebar-foreground/60 ml-auto">
                {daysLeft} {t("days left", "દિવસ બાકી")}
              </span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-sidebar-accent overflow-hidden">
              <div
                className="h-full rounded-full gradient-primary transition-all duration-500"
                style={{ width: `${daysPercent}%` }}
              />
            </div>
          </div>
          )}

          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-2 rounded-lg text-sm font-medium text-sidebar-foreground/80 hover:bg-sidebar-accent transition-colors"
          >
            <LogOut size={18} />
            <span>{t("Logout", "લૉગઆઉટ")}</span>
          </button>
        </div>
      </aside>
    </div>
  );
}
